import styled, { css } from "styled-components";
import DatePicker from "react-datepicker";

// ── Griffle tokens (inlined to avoid circular imports) ────────────────────────
const G = {
  primary: "#539a18",
  primaryDark: "#3f7a10",
  surface: "#ffffff",
  pageBg: "#f4f7f1",
  border: "#d4dfd0",
  ink: "#1a1a1a",
  inkMuted: "#52525b",
  filterBg: "#1b3a0a",
  blue: "#165b95",
  navyBg: "#0e2d54",
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
};

interface SkinProps {
  $isGriffle?: boolean;
}

export const StyledReportFilter = styled.div<SkinProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0;
  padding: 25px;
  background-color: ${({ $isGriffle }) => ($isGriffle ? G.filterBg : G.navyBg)};
  border-radius: 12px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  gap: 20px;
`;

export const StyledDateRangeWrapper = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

export const StyledDatePickerInputGroup = styled.div<SkinProps>`
  position: relative;
  display: flex;
  flex-direction: column;

  label {
    position: absolute;
    top: -18px;
    font-size: 13px;
    color: #ffffff;
    font-family: ${({ $isGriffle }) => ($isGriffle ? G.font : '"Baloo 2", cursive')};
    opacity: 0.7;
    font-weight: 500;
  }
`;

export const StyledDatePicker = styled(DatePicker)<SkinProps>`
  font-weight: ${({ $isGriffle }) => ($isGriffle ? "500" : "700")};
  font-size: ${({ $isGriffle }) => ($isGriffle ? "14px" : "20px")};
  color: ${({ $isGriffle }) => ($isGriffle ? G.ink : G.blue)};
  font-family: ${({ $isGriffle }) => ($isGriffle ? `${G.font} !important` : '"Baloo 2", cursive !important')};
  background: ${G.surface};
  border: ${({ $isGriffle }) => ($isGriffle ? `1.5px solid ${G.border}` : "2px solid #38a6de")};
  border-radius: ${({ $isGriffle }) => ($isGriffle ? "8px" : "20px")};
  height: ${({ $isGriffle }) => ($isGriffle ? "36px" : "43px")};
  min-width: 180px;
  padding: 0 40px 0 ${({ $isGriffle }) => ($isGriffle ? "12px" : "26px")};
  position: relative;
  transition: border-color 0.15s;

  ${({ $isGriffle }) =>
    $isGriffle &&
    css`
      &:focus {
        outline: none;
        border-color: ${G.primary};
        box-shadow: 0 0 0 3px ${G.primary}22;
      }
    `}

  &::placeholder {
    color: ${({ $isGriffle }) => ($isGriffle ? G.inkMuted : G.blue)};
  }
`;

interface StyledTabButtonProps extends SkinProps {
  active: boolean;
}

export const StyledTabsContainer = styled.div<SkinProps>`
  display: flex;
  justify-content: ${({ $isGriffle }) => ($isGriffle ? "flex-start" : "space-around")};
  padding: ${({ $isGriffle }) => ($isGriffle ? "0 0 0 4px" : "25px")};
  border-radius: ${({ $isGriffle }) => ($isGriffle ? "0" : "12px")};
  margin-bottom: ${({ $isGriffle }) => ($isGriffle ? "0" : "30px")};
  box-shadow: ${({ $isGriffle }) => ($isGriffle ? "none" : "0 0 10px rgb(0 0 0 / 10%)")};
  border-bottom: ${({ $isGriffle }) => ($isGriffle ? `2px solid ${G.border}` : "none")};
  gap: ${({ $isGriffle }) => ($isGriffle ? "4px" : "0")};
`;

export const StyledTabButton = styled.button<StyledTabButtonProps>`
  font-size: ${({ $isGriffle }) => ($isGriffle ? "14px" : "20px")};
  font-weight: ${({ $isGriffle, active }) => ($isGriffle ? (active ? "600" : "500") : "bold")};
  white-space: nowrap;
  color: ${({ $isGriffle, active }) =>
    $isGriffle ? (active ? G.primary : G.inkMuted) : active ? G.blue : "rgb(13 45 84 / 25%)"};
  font-family: ${({ $isGriffle }) => ($isGriffle ? G.font : '"Baloo 2", cursive !important')};
  background: none;
  border: none;
  padding: ${({ $isGriffle }) => ($isGriffle ? "10px 16px" : "10px 20px")};
  cursor: pointer;
  border-bottom: ${({ $isGriffle, active }) =>
    $isGriffle
      ? active
        ? `2px solid ${G.primary}`
        : "2px solid transparent"
      : active
      ? "3px solid #165b95"
      : "2px solid transparent"};
  transition: color 0.15s, border-bottom-color 0.15s;
  margin-bottom: -2px;

  &:hover {
    color: ${({ $isGriffle }) => ($isGriffle ? G.primary : G.blue)};
  }
`;

export const StyledCurrentFilterDescription = styled.div<SkinProps>`
  display: flex;
  justify-content: flex-end;
  padding-right: 5px;
  padding-bottom: 5px;

  p {
    font-size: ${({ $isGriffle }) => ($isGriffle ? "13px" : "16px")};
    color: ${({ $isGriffle }) => ($isGriffle ? G.inkMuted : "#0d2d54")};
    font-family: ${({ $isGriffle }) => ($isGriffle ? G.font : '"Baloo 2", cursive !important')};
  }
`;

export const StyledExportContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 5px;
  padding-bottom: 5px;
`;
